import { Sparkles } from "lucide-react";
import { ReviewRow } from "./atoms";
import { useCurrency } from "../../contexts/CurrencyContext";

/**
 * Step 4 — "Review & Pay": read-only summary using the LIVE pricing object
 * from /campaigns/preview-pricing.
 *
 * Currency presentation: when the active CurrencyContext currency matches
 * the `fx_adjusted` block on the pricing payload, every figure on this card
 * flips to the localized symbol + magnitude + ISO code. The Stripe checkout
 * is invoked with the same `displayCcy` so what the user sees here is
 * exactly what their card is billed.
 */
export default function StepReview({ form, pricing }) {
  const { currency: displayCcy, formatLocal } = useCurrency();
  const fmtUsd = (n) => `$${Number(n || 0).toFixed(2)} USD`;
  const fx = pricing?.fx_adjusted;
  const usingFx = !!(fx && fx.display_currency === displayCcy);
  // Local formatter that also includes the ISO suffix so the Review card
  // remains explicit about the currency at billing time (Stripe will bill
  // in this exact currency).
  const fmtLocalWithIso = (n) => `${formatLocal(n)} ${displayCcy}`;
  const fmtMoney = usingFx ? fmtLocalWithIso : fmtUsd;
  const pVal = (key) => (usingFx ? fx?.[key] : pricing?.[key]);
  const rateNote = usingFx && fx?.exchange_rate && displayCcy !== "USD"
    ? `Billed in ${displayCcy} at 1 USD = ${fx.exchange_rate} ${displayCcy} · `
    : "";
  return (
    <div className="space-y-6" data-testid="step-review">
      <ReviewRow label="Title" value={form.title} />
      <ReviewRow label="Modality" value={form.modality} />
      <ReviewRow label="Target completes" value={form.target_completes} />
      <ReviewRow
        label="Study parameters"
        value={`${form.expected_ir}% IR · ${form.expected_loi} min LOI · ${form.field_time}-day field`}
      />
      <ReviewRow label="Network raw cost / complete" value={fmtMoney(pVal("raw_cost_per_complete"))} />
      <ReviewRow label="Platform fees / complete" value={fmtMoney(pVal("markup_per_complete"))} />
      <ReviewRow label="Final cost / complete" value={fmtMoney(pVal("cost_per_complete"))} />
      <ReviewRow
        label="Audience mix"
        value={`Internal ${form.audience_sourcing_mix.internal_panel}% • Programmatic ${form.audience_sourcing_mix.programmatic_cloudresearch}% • Intercept ${form.audience_sourcing_mix.client_intercept}%`}
      />
      <ReviewRow label="Stimulus" value={form.stimulus_type === "none" ? "None" : `${form.stimulus_type}`} />
      <div className="bg-[var(--rl-text)] text-white p-6 mt-6">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold opacity-70">Total to authorize</div>
        <div className="font-display font-black text-4xl mt-2" data-testid="review-total">{fmtMoney(pVal("total_budget"))}</div>
        {usingFx && displayCcy !== "USD" && (
          <div className="text-[10px] opacity-60 mt-2 font-mono" data-testid="review-fx-disclosure">
            Source of truth: ${Number(pricing?.total_budget || 0).toFixed(2)} USD · live exchange 1 USD = {fx.exchange_rate} {displayCcy}
          </div>
        )}
        <div className="text-xs opacity-70 mt-2">{rateNote}Charged via Stripe. Our Programmatic Panel funds the participant payouts from our master balance the moment the webhook confirms.</div>
      </div>
      <div className="flex items-start gap-2 text-xs text-[var(--rl-text-secondary)]">
        <Sparkles className="w-4 h-4 text-[var(--rl-brand)] flex-shrink-0 mt-0.5" />
        Modality `{form.modality}` drives hardware preflight (text = none, audio = mic, video = mic + 1280×720 cam) and {fmtMoney(pVal("markup_per_complete"))} per-complete platform fees.
      </div>
    </div>
  );
}
